import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Input, Select } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { weeksToOosLabel, WEEKS_TO_OOS_UNBOUNDED } from "../api/pipeline";
import { imageUrl, LazyProductClient, type LazyProductDTO } from "../api/product";
import {
  TransferPlanClient,
  transferRowId,
  type TransferSuggestionDTO,
} from "../api/transfer";
import { SETTINGS_PATHS, fetchSingleRow, settingsKeys, type ProductionSettingsDTO } from "../api/settings";
import { NexpimView } from "../views";
import { CoverBar } from "../components/CoverBar";
import { ProductLink } from "../components/ProductLink";

/**
 * How short the destination is before rounding.
 *
 * The suggestion rounds to fives and is capped by what upstream can spare, so
 * showing the raw need beside it is what makes those two adjustments visible
 * rather than mysterious.
 */
function shortfallOf(row: TransferSuggestionDTO, weeks: number): number {
  return Math.max(Math.ceil(row.weekly_sales * weeks) - row.destination_available, 0);
}

/**
 * Weeks of stock left, drawn against the cover being asked for.
 *
 * A bar rather than a number because this is the column people scan: the eye
 * finds a short bar without reading, and reading every row was the old
 * screen's problem.
 */
function WeeksLeft({ weeks, target }: { weeks: number; target: number }) {
  const { t } = useTranslation();
  const unbounded = weeks >= WEEKS_TO_OOS_UNBOUNDED;
  const out = weeks <= 0;

  const text = out ? "text-risk font-medium" : unbounded ? "text-ink-faint" : weeks < target ? "text-warn" : "text-ink";

  return (
    <span
      className="flex items-center gap-2"
      title={
        unbounded
          ? t("pipeline.no-demand", "No recent demand, nothing to run out of.")
          : undefined
      }
    >
      <CoverBar weeks={weeks} target={target} />
      <span className={`tabular text-[13px] ${text}`}>
        {out ? t("transfer.out", "OUT") : weeksToOosLabel(weeks)}
      </span>
    </span>
  );
}


/**
 * Suggested transfers into one warehouse.
 *
 * Deliberately carries no production data. A transfer and a production lot
 * answer different questions, and the point of separating them is that a
 * warehouse can be days from empty while the company already owns the units, in
 * which case building more would be waste.
 *
 * One destination at a time, because that is the shape of the job: you are
 * filling a shipment to a particular place, not surveying the network. Every
 * product that warehouse stocks is listed, including the ones needing nothing,
 * so the list can be checked rather than trusted — a product sitting just above
 * the line is exactly what you want to see before deciding how full to make the
 * load.
 */
export function TransferPlanPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();

  const client = useMemo(() => new TransferPlanClient(http), [http]);
  const products = useMemo(() => new LazyProductClient(http), [http]);
  const allowed = hasView(NexpimView.PRODUCTION_PIPELINE);

  const [destination, setDestination] = useState<number | null>(null);
  // Cover and reserve start at the production settings' defaults; what is
  // typed here changes this screen only, for this visit.
  const settingsQuery = useQuery({
    queryKey: settingsKeys.production,
    queryFn: () => fetchSingleRow<ProductionSettingsDTO>(http, SETTINGS_PATHS.production),
    enabled: allowed,
    staleTime: 10 * 60_000,
  });
  const [weeksOverride, setWeeks] = useState<number | null>(null);
  const [reserveOverride, setReserveWeeks] = useState<number | null>(null);
  const weeks = weeksOverride ?? settingsQuery.data?.transfer_cover_weeks ?? null;
  const reserveWeeks = reserveOverride ?? settingsQuery.data?.transfer_reserve_weeks ?? null;

  const destinationsQuery = useQuery({
    queryKey: ["warehouse", "transfer-destinations"],
    queryFn: () => client.destinations(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  // Settle on a destination as soon as we know which ones exist. Whichever comes
  // first is fine: the server sorts them by code and there is only one today.
  useEffect(() => {
    if (destination === null && destinationsQuery.data?.length) {
      setDestination(destinationsQuery.data[0]!.id);
    }
  }, [destination, destinationsQuery.data]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["warehouse", "transfer-plan", destination, weeks, reserveWeeks],
    queryFn: async () => {
      const plan = await client.getPlan(destination!, weeks!, reserveWeeks!);
      // One row per product, but ask for each product once regardless.
      const ids = [...new Set(plan.map((row) => row.product_id))];
      const productRows = ids.length ? await products.findByIds(ids) : [];
      return { plan, productMap: new Map(productRows.map((row) => [row.id, row])) };
    },
    enabled: allowed && destination !== null && weeks !== null && reserveWeeks !== null,
  });

  const rows = data?.plan ?? [];
  const needed = rows.filter((row) => row.suggested_units > 0);
  const totalUnits = needed.reduce((sum, row) => sum + row.suggested_units, 0);

  const controls = (
    // Beside the search box rather than in the top bar: the horizon changes what
    // every number below it means, so it belongs with the grid it rewrites.
    <div className="flex items-center gap-1.5">
      <Select
        aria-label={t("transfer.destination", "Destination")}
        value={destination === null ? "" : String(destination)}
        options={(destinationsQuery.data ?? []).map((warehouse) => ({
          value: String(warehouse.id),
          label: warehouse.code,
        }))}
        onChange={(event) => setDestination(Number(event.target.value))}
        className="h-8 w-28"
      />
      {/* Both boxes are labelled. With only the second one named, "4 keep 4"
          read as though the word belonged to the box on its left, and people
          changed the wrong number. */}
      <span className="text-[12px] text-ink-faint">{t("transfer.cover-label", "cover")}</span>
      <Input
        type="number"
        min={1}
        max={52}
        aria-label={t("transfer.cover-weeks", "Inventory weeks")}
        value={weeks === null ? "" : String(weeks)}
        onChange={(event) => {
          const value = Number(event.target.value);
          if (Number.isFinite(value) && value >= 1 && value <= 52) setWeeks(value);
        }}
        className="h-8 w-16"
        title={t("transfer.cover-weeks.hint", "Weeks of stock the destination should end up holding")}
      />
      <span className="ml-1 text-[12px] text-ink-faint">
        {t("transfer.reserve-label", "keep")}
      </span>
      <Input
        type="number"
        min={0}
        max={52}
        aria-label={t("transfer.reserve-weeks", "Reserve weeks")}
        value={reserveWeeks === null ? "" : String(reserveWeeks)}
        onChange={(event) => {
          const value = Number(event.target.value);
          if (Number.isFinite(value) && value >= 0 && value <= 52) setReserveWeeks(value);
        }}
        className="h-8 w-16"
        title={t("transfer.reserve-weeks.hint", "Weeks of its own demand each source keeps back")}
      />
      <span className="text-[12px] text-ink-muted">
        {t("transfer.cover-weeks", "Inventory weeks")}
      </span>
    </div>
  );

  // The column is named after the warehouse it describes; "Destination" was
  // accurate and told nobody anything.
  const destinationCode = useMemo(
    () =>
      (destinationsQuery.data ?? []).find((warehouse) => warehouse.id === destination)?.code ??
      t("column.transfer-on-hand", "On hand"),
    [destinationsQuery.data, destination, t],
  );

  const cols = useMemo(() => {
    const c = defineColumns<TransferSuggestionDTO>();
    const productMap = data?.productMap ?? new Map<number, LazyProductDTO>();

    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => {
          const url = imageUrl(productMap.get(row.product_id)?.image ?? null);
          return url ? (
            <img src={url} alt="" loading="lazy" className="size-12 rounded object-cover" />
          ) : (
            <span className="block size-12 rounded bg-surface-sunk" />
          );
        },
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "product",
        (row) => `${row.sku ?? ""} ${row.product_name ?? ""}`,
        (row) => (
          <span className="flex flex-col py-0.5">
            <ProductLink id={row.product_id} className="font-medium">
              {row.sku}
            </ProductLink>
            <span className="text-[11px] text-ink-faint">{row.product_name}</span>
          </span>
        ),
        { headerKey: "column.product", priority: 1 },
      ),
      // Velocity in the market(s) the destination ships the product to, with
      // the rank there — the products page's velocity cell, per market. Sorts
      // on the first market's velocity.
      c.custom(
        "market_velocity",
        (row) => row.markets?.[0]?.velocity ?? 0,
        (row) => (
          <span className="flex flex-col items-end gap-0.5 leading-tight">
            {(row.markets ?? []).map((sales) => (
              <span key={sales.market} className="flex flex-col items-end">
                <span className="text-[14px] font-semibold text-ink tabular-nums">{truncate2(sales.velocity)}</span>
                <span className="text-[11px] font-medium text-accent tabular-nums">
                  {sales.market}
                  {sales.sales_rank
                    ? ` ${
                        row.product_type === "BUNDLE"
                          ? t("product.rank.bundle", "Bundle #{{rank}}", { rank: sales.sales_rank })
                          : t("product.rank.single", "#{{rank}}", { rank: sales.sales_rank })
                      }`
                    : ""}
                </span>
              </span>
            ))}
          </span>
        ),
        { headerKey: "column.transfer-market-velocity", filter: "range", align: "right", priority: 2 },
      ),
      // Weeks left, as a bar filled against the cover you asked for. The bar is
      // the point: you see the short ones without reading a number, which is
      // what "which rows matter" actually needs.
      c.custom(
        "weeks_remaining",
        (row) => row.weeks_remaining,
        (row) => <WeeksLeft weeks={row.weeks_remaining} target={weeks ?? 0} />,
        { headerKey: "column.transfer-weeks-left", filter: "none", priority: 1 },
      ),

      // Named after the warehouse it describes. "Destination" was accurate and
      // told you nothing; the code is what people call it.
      c.custom(
        "destination",
        (row) => row.destination_available,
        (row) => (
          <span
            className="flex flex-col items-end leading-tight"
            title={
              row.destination_on_hand === undefined
                ? undefined
                : t(
                    "transfer.on-hand-breakdown",
                    "{{onHand}} on hand, {{reserved}} reserved, {{ordered}} ordered",
                    {
                      onHand: row.destination_on_hand,
                      reserved: row.destination_reserved ?? 0,
                      ordered: row.destination_in_order ?? 0,
                    },
                  )
            }
          >
            {/* Available leads, because it is the number the decision turns
                on. On hand sits under it so the gap between the two, the stock
                that exists but is already spoken for, is visible. */}
            <span
              className={
                row.destination_available < 0
                  ? "tabular text-[13px] font-medium text-risk"
                  : "tabular text-[13px] text-ink"
              }
            >
              {t("transfer.avail", "Avail: {{count}}", { count: row.destination_available })}
            </span>
            {/* Absent on a backend older than these fields. Better to drop the
                line than to print a label with nothing after it. */}
            {row.destination_on_hand !== undefined && (
              <span className="tabular text-[11px] text-ink-faint">
                {t("transfer.on-hand", "On hand: {{count}}", { count: row.destination_on_hand })}
              </span>
            )}
          </span>
        ),
        { header: destinationCode, filter: "none", align: "right", priority: 2 },
      ),

      // The answer. Hover it for the raw shortfall it was rounded up from; the
      // weekly rate it came from is in the Velocity column.
      c.custom(
        "suggested_units",
        (row) => row.suggested_units,
        (row) => {
          // The suggestion is the need, uncapped, so a row can ask for more
          // than exists. That is the case worth marking: it is a purchasing
          // problem, not a shipping one.
          const short = row.suggested_units > row.source_safe;
          const seeded = row.weekly_sales <= 0 && row.suggested_units > 0;
          return (
            <span className="flex flex-col items-end leading-tight">
              {row.suggested_units > 0 ? (
                <span className="flex items-center gap-1.5">
                  {short && (
                    <span title={t("transfer.short-upstream", "More than upstream can spare")}>
                      <AlertTriangle className="size-3.5 text-warn" />
                    </span>
                  )}
                  <span
                    className={
                      short
                        ? "tabular text-lg font-semibold text-warn"
                        : "tabular text-lg font-semibold text-accent-strong"
                    }
                    // The raw shortfall the figure was rounded up from: the
                    // first thing anyone asks when the two disagree.
                    title={t("transfer.rounded-from", "Rounded up from {{needs}}", {
                      needs: shortfallOf(row, weeks ?? 0),
                    })}
                  >
                    {row.suggested_units}
                  </span>
                </span>
              ) : (
                <span className="tabular text-lg text-ink-faint">0</span>
              )}
              {seeded && (
                <Badge variant="info" size="sm">
                  <span
                    title={t(
                      "transfer.seed-hint",
                      "No recent sales and nothing on hand, so there is no shortfall to compute. Suggesting a starter quantity.",
                    )}
                  >
                    {t("transfer.no-sales-no-stock", "no sales, no stock")}
                  </span>
                </Badge>
              )}
            </span>
          );
        },
        { headerKey: "column.transfer-to-ship", filter: "none", align: "right", priority: 1 },
      ),

      // What upstream can part with, and what it is holding back. The second
      // line is what makes the first mean anything: 140 free is only sensible
      // once you can see that 12 is being kept for its own demand.
      c.custom(
        "can_send",
        (row) => row.source_safe,
        (row) => {
          const capped = row.suggested_units > row.source_safe;
          return (
            <span className="flex flex-col items-end leading-tight">
              <span
                className={
                  capped
                    ? "tabular text-[13px] font-medium text-warn"
                    : "tabular text-[13px] text-ink"
                }
              >
                {t("transfer.free-to-send", "{{count}} free", { count: row.source_safe })}
              </span>
              <span className="tabular text-[11px] text-ink-faint">
                {t("transfer.kept-back", "{{count}} kept back", {
                  count: Math.max(row.source_total - row.source_safe, 0),
                })}
              </span>
            </span>
          );
        },
        { headerKey: "column.transfer-can-send", filter: "none", align: "right", priority: 2 },
      ),
    ];
  }, [data, t, weeks, destinationCode]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.transfers", "Transfers")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.transfers", "Transfers")}
        subtitle={t(
          "transfer.subtitle",
          "Stock to move between warehouses.",
        )}
        badges={
          needed.length ? (
            <>
              <Badge variant="accent" size="sm">
                {t("transfer.badge.products", "To ship")}: {needed.length}
              </Badge>
              <Badge variant="neutral" size="sm">
                {t("transfer.badge.units", "Units")}: {totalUnits}
              </Badge>
            </>
          ) : null
        }
      />

      {error ? (
        <div className="p-4 lg:p-6">
          <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
            {t("error.generic", "Something went wrong. Please try again.")}
          </p>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={cols}
          getRowId={transferRowId}
          initialSorting={[{ id: "weeks_remaining", desc: false }]}
          loading={isLoading || destinationsQuery.isLoading}
          toolbarExtra={controls}
          emptyMessage={t(
            "transfer.empty-list",
            "Nothing stocked here yet, or no warehouse can ship to it.",
          )}
        />
      )}
    </>
  );
}

/** Two decimals, cut rather than rounded — the products page's velocity format. */
function truncate2(value: number): number {
  return Math.trunc(value * 100) / 100;
}
